const METADATA_FIELDS = ["name", "description", "privacy", "type", "difficulty", "planType"];

function normalizeString(value) {
  return String(value || "").trim().toLowerCase();
}

function toComparableValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return normalizeString(value);
}

function splitLabel(split) {
  return split?.name || "Unnamed workout";
}

function splitKey(split) {
  return `${normalizeString(split?.name)}::${split?.isRestSplit ? "rest" : "workout"}`;
}

function exerciseSignature(exercise) {
  return [
    exercise?.type || "",
    exercise?.exercise || "",
    exercise?.superSetName || "",
    exercise?.sets ?? "",
    exercise?.reps ?? "",
    exercise?.time ?? "",
    exercise?.distance ?? "",
    exercise?.rest ?? "",
    exercise?.tempo ?? "",
  ].join("|");
}

function buildMultiset(items = []) {
  return items.reduce((acc, item) => {
    const key = item;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
}

function diffMultiset(beforeItems = [], afterItems = []) {
  const beforeSet = buildMultiset(beforeItems);
  const afterSet = buildMultiset(afterItems);
  const allKeys = new Set([...Object.keys(beforeSet), ...Object.keys(afterSet)]);
  let added = 0;
  let removed = 0;

  allKeys.forEach((key) => {
    const beforeCount = beforeSet[key] || 0;
    const afterCount = afterSet[key] || 0;
    if (afterCount > beforeCount) {
      added += afterCount - beforeCount;
    } else if (beforeCount > afterCount) {
      removed += beforeCount - afterCount;
    }
  });

  return { added, removed };
}

function buildWorkoutExerciseIndex(snapshot) {
  const splits = snapshot?.splits || [];
  const exercises = snapshot?.exercises || [];
  return splits.reduce((acc, split) => {
    const key = splitKey(split);
    const splitExercises = exercises
      .filter((exercise) => exercise.splitId === split.id)
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .map(exerciseSignature);
    acc[key] = splitExercises;
    return acc;
  }, {});
}

export function buildVersionDiff({ fromSnapshot, toSnapshot }) {
  const fromPlan = fromSnapshot?.plan || {};
  const toPlan = toSnapshot?.plan || {};
  const metadataChanges = METADATA_FIELDS.reduce((changes, field) => {
    const before = fromPlan[field];
    const after = toPlan[field];
    if (toComparableValue(before) !== toComparableValue(after)) {
      changes.push({ field, before, after });
    }
    return changes;
  }, []);

  const fromWorkouts = (fromSnapshot?.splits || []).map(splitKey);
  const toWorkouts = (toSnapshot?.splits || []).map(splitKey);
  const fromWorkoutSet = new Set(fromWorkouts);
  const toWorkoutSet = new Set(toWorkouts);
  const workoutsAdded = [...toWorkoutSet].filter((key) => !fromWorkoutSet.has(key));
  const workoutsRemoved = [...fromWorkoutSet].filter((key) => !toWorkoutSet.has(key));
  const commonWorkouts = [...toWorkoutSet].filter((key) => fromWorkoutSet.has(key));

  const fromWorkoutOrder = (fromSnapshot?.splits || []).map(splitKey);
  const toWorkoutOrder = (toSnapshot?.splits || []).map(splitKey);
  const fromCommonOrder = fromWorkoutOrder.filter((key) => commonWorkouts.includes(key));
  const toCommonOrder = toWorkoutOrder.filter((key) => commonWorkouts.includes(key));
  const workoutOrderChanged =
    fromCommonOrder.length > 1 &&
    JSON.stringify(fromCommonOrder) !== JSON.stringify(toCommonOrder);

  const fromWorkoutExercises = buildWorkoutExerciseIndex(fromSnapshot);
  const toWorkoutExercises = buildWorkoutExerciseIndex(toSnapshot);
  const exerciseChangesByWorkout = commonWorkouts
    .map((workoutKey) => {
      const diff = diffMultiset(
        fromWorkoutExercises[workoutKey] || [],
        toWorkoutExercises[workoutKey] || [],
      );
      return {
        workoutKey,
        added: diff.added,
        removed: diff.removed,
      };
    })
    .filter((change) => change.added > 0 || change.removed > 0);

  const exerciseAdded = exerciseChangesByWorkout.reduce((sum, item) => sum + item.added, 0);
  const exerciseRemoved = exerciseChangesByWorkout.reduce((sum, item) => sum + item.removed, 0);

  const fromSplitNameByKey = Object.fromEntries(
    (fromSnapshot?.splits || []).map((split) => [splitKey(split), splitLabel(split)]),
  );
  const toSplitNameByKey = Object.fromEntries(
    (toSnapshot?.splits || []).map((split) => [splitKey(split), splitLabel(split)]),
  );

  return {
    summary: {
      metadataChanges: metadataChanges.length,
      workoutsAdded: workoutsAdded.length,
      workoutsRemoved: workoutsRemoved.length,
      workoutOrderChanged,
      exerciseAdded,
      exerciseRemoved,
    },
    details: {
      metadataChanges,
      workouts: {
        added: workoutsAdded.map((key) => toSplitNameByKey[key] || key),
        removed: workoutsRemoved.map((key) => fromSplitNameByKey[key] || key),
        orderChanged: workoutOrderChanged,
      },
      exercisesByWorkout: exerciseChangesByWorkout.map((change) => ({
        workout: toSplitNameByKey[change.workoutKey] || fromSplitNameByKey[change.workoutKey] || change.workoutKey,
        added: change.added,
        removed: change.removed,
      })),
    },
  };
}
